/**
 * Layout calculation module for chart regions.
 * Determines chart areas (price, volume, axes) based on dimensions and configuration.
 */

import type {
  ChartArea,
  ChartLayout,
  ChartPadding,
  ChartTypography,
  ChartVolumeConfig,
  LayoutConfig,
  ValidationResult,
} from "~types/types";

// ==================== Constants ====================

/**
 * Default padding values.
 */
export const DEFAULT_PADDING: Required<ChartPadding> = {
  top: 20,
  right: 0,
  bottom: 5,
  left: 0,
};

/**
 * Default X-axis height in pixels.
 * Sized to accommodate primary labels, secondary labels (date/year), and crosshair overlay.
 */
export const DEFAULT_X_AXIS_HEIGHT = 45;

/**
 * Default Y-axis width in pixels.
 */
export const DEFAULT_Y_AXIS_WIDTH = 60;

/**
 * Default volume height ratio (20% of available space).
 */
export const DEFAULT_VOLUME_HEIGHT_RATIO = 0.2;

/**
 * Minimum height for price area in pixels.
 */
export const MIN_PRICE_HEIGHT = 100;

/**
 * Minimum height for volume area in pixels.
 */
export const MIN_VOLUME_HEIGHT = 40;

/**
 * Minimum chart dimensions.
 */
export const MIN_CHART_WIDTH = 200;
export const MIN_CHART_HEIGHT = 100;

// ==================== Typography ====================

/**
 * Base font sizes for reference chart size (800x400).
 */
const BASE_CHART_WIDTH = 800;
const BASE_CHART_HEIGHT = 400;
const BASE_AXIS_LABEL_SIZE = 14;
const BASE_SECONDARY_LABEL_SIZE = 14;
const BASE_CHAR_WIDTH = 6.5;

/**
 * Font size bounds.
 */
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 16;

/**
 * Calculate typography settings based on chart dimensions.
 * Font sizes scale proportionally with the chart size.
 */
export function calculateTypography(
  width: number,
  height: number
): ChartTypography {
  // Calculate scale factor based on geometric mean of width and height ratios
  const widthRatio = width / BASE_CHART_WIDTH;
  const heightRatio = height / BASE_CHART_HEIGHT;
  const scaleFactor = Math.sqrt(widthRatio * heightRatio);

  // Scale font sizes with bounds
  const axisLabelSize = Math.round(
    Math.max(
      MIN_FONT_SIZE,
      Math.min(MAX_FONT_SIZE, BASE_AXIS_LABEL_SIZE * scaleFactor)
    )
  );

  const secondaryLabelSize = Math.round(
    Math.max(
      MIN_FONT_SIZE,
      Math.min(MAX_FONT_SIZE, BASE_SECONDARY_LABEL_SIZE * scaleFactor)
    )
  );

  // Character width scales with font size
  const fontSizeRatio = axisLabelSize / BASE_AXIS_LABEL_SIZE;
  const charWidth = BASE_CHAR_WIDTH * fontSizeRatio;

  return {
    axisLabelSize,
    secondaryLabelSize,
    charWidth,
    lineHeight: 1.4,
  };
}

// ==================== Layout Calculation ====================

/**
 * Calculate height distribution between price and volume areas.
 */
export function calculateHeightDistribution(
  availableHeight: number,
  _volumeConfig?: ChartVolumeConfig
): { priceHeight: number; volumeHeight: number } {
  // Volume overlay renders inside price area, no separate allocation needed
  return { priceHeight: availableHeight, volumeHeight: 0 };
}

/**
 * Calculate chart layout from configuration.
 */
export function calculateLayout(config: LayoutConfig): ChartLayout {
  const {
    width,
    height,
    padding = {},
    volume,
    yAxisPosition = "right",
    xAxisHeight = DEFAULT_X_AXIS_HEIGHT,
    yAxisWidth = DEFAULT_Y_AXIS_WIDTH,
  } = config;

  // Merge with defaults
  const pad: Required<ChartPadding> = { ...DEFAULT_PADDING, ...padding };

  // Calculate available space for chart content
  const availableWidth = width - pad.left - pad.right - yAxisWidth;
  const availableHeight = height - pad.top - pad.bottom - xAxisHeight;

  // Calculate height distribution
  const { priceHeight, volumeHeight } = calculateHeightDistribution(
    availableHeight,
    volume
  );

  // Calculate area positions based on yAxisPosition
  const chartX = yAxisPosition === "left" ? pad.left + yAxisWidth : pad.left;
  const yAxisX =
    yAxisPosition === "left" ? pad.left : width - pad.right - yAxisWidth;

  // Build layout object
  const priceArea = {
    x: chartX,
    y: pad.top,
    width: availableWidth,
    height: priceHeight,
  };

  const volumeArea =
    volumeHeight > 0
      ? {
          x: chartX,
          y: pad.top + priceHeight,
          width: availableWidth,
          height: volumeHeight,
        }
      : null;

  const xAxisArea = {
    x: chartX,
    y: pad.top + priceHeight + volumeHeight,
    width: availableWidth,
    height: xAxisHeight,
  };

  const yAxisArea = {
    x: yAxisX,
    y: pad.top,
    width: yAxisWidth,
    height: priceHeight + volumeHeight,
  };

  // Calculate typography based on chart dimensions
  const typography = calculateTypography(width, height);

  return {
    width,
    height,
    priceArea,
    volumeArea,
    xAxisArea,
    yAxisArea,
    chartWidth: availableWidth,
    chartHeight: priceHeight + volumeHeight,
    typography,
  };
}

// ==================== Validation ====================

/**
 * Validate a chart layout.
 */
export function validateLayout(layout: ChartLayout): ValidationResult {
  const errors: string[] = [];

  // Check positive dimensions
  if (layout.width <= 0 || layout.height <= 0) {
    errors.push("Container dimensions must be positive");
  }

  if (layout.priceArea.width <= 0 || layout.priceArea.height <= 0) {
    errors.push("Price area dimensions must be positive");
  }

  if (layout.xAxisArea.width <= 0 || layout.xAxisArea.height <= 0) {
    errors.push("X-axis area dimensions must be positive");
  }

  if (layout.yAxisArea.width <= 0 || layout.yAxisArea.height <= 0) {
    errors.push("Y-axis area dimensions must be positive");
  }

  if (layout.volumeArea) {
    if (layout.volumeArea.width <= 0 || layout.volumeArea.height <= 0) {
      errors.push("Volume area dimensions must be positive");
    }
  }

  // Check minimum dimensions
  if (layout.chartWidth < MIN_CHART_WIDTH) {
    errors.push(`Chart width must be at least ${MIN_CHART_WIDTH}px`);
  }

  if (layout.chartHeight < MIN_CHART_HEIGHT) {
    errors.push(`Chart height must be at least ${MIN_CHART_HEIGHT}px`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// ==================== Area Detection ====================

/**
 * Determine which area a point falls into.
 */
export function getAreaAtPoint(
  layout: ChartLayout,
  x: number,
  y: number
): ChartArea {
  // Check if outside container
  if (x < 0 || y < 0 || x > layout.width || y > layout.height) {
    return "outside";
  }

  // Check price area
  const { priceArea, volumeArea, xAxisArea, yAxisArea } = layout;

  if (
    x >= priceArea.x &&
    x < priceArea.x + priceArea.width &&
    y >= priceArea.y &&
    y < priceArea.y + priceArea.height
  ) {
    return "price";
  }

  // Check volume area
  if (volumeArea) {
    if (
      x >= volumeArea.x &&
      x < volumeArea.x + volumeArea.width &&
      y >= volumeArea.y &&
      y < volumeArea.y + volumeArea.height
    ) {
      return "volume";
    }
  }

  // Check X-axis area
  if (
    x >= xAxisArea.x &&
    x < xAxisArea.x + xAxisArea.width &&
    y >= xAxisArea.y &&
    y < xAxisArea.y + xAxisArea.height
  ) {
    return "xAxis";
  }

  // Check Y-axis area
  if (
    x >= yAxisArea.x &&
    x < yAxisArea.x + yAxisArea.width &&
    y >= yAxisArea.y &&
    y < yAxisArea.y + yAxisArea.height
  ) {
    return "yAxis";
  }

  // Must be in padding
  return "padding";
}

// ==================== Coordinate Conversion ====================

/**
 * Convert screen coordinates to chart coordinates (relative to priceArea).
 * Returns null if point is outside the price area.
 */
export function screenToChartCoords(
  layout: ChartLayout,
  screenX: number,
  screenY: number
): { chartX: number; chartY: number } | null {
  const { priceArea } = layout;

  // Check if within price area bounds
  if (
    screenX < priceArea.x ||
    screenX > priceArea.x + priceArea.width ||
    screenY < priceArea.y ||
    screenY > priceArea.y + priceArea.height
  ) {
    return null;
  }

  return {
    chartX: screenX - priceArea.x,
    chartY: screenY - priceArea.y,
  };
}

/**
 * Convert chart coordinates back to screen coordinates.
 */
export function chartToScreenCoords(
  layout: ChartLayout,
  chartX: number,
  chartY: number
): { screenX: number; screenY: number } {
  const { priceArea } = layout;

  return {
    screenX: priceArea.x + chartX,
    screenY: priceArea.y + chartY,
  };
}
